import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslationApp } from "../../../lib/i18next";
import { ConfirmAlert } from "../../../components/Alerts/Alerts";
import {
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import { BORDER_RADIUS, IS_ADLE_DOMAIN } from "../../../utils/constants";
import {
  AI_COMPETITORS_AGENDA_CONFIRM_CTA,
  AI_COMPETITORS_AGENDA_CONFIRM_TEXT,
  AI_COMPETITORS_AGENDA_CONFIRM_TITLE,
  AI_COMPETITORS_RESULTS_MUTED,
  AI_COMPETITORS_RESULTS_NO_ADS_ALERT_BODY,
  AI_COMPETITORS_RESULTS_NO_ADS_ALERT_TITLE,
  AI_COMPETITORS_RESULTS_NO_ADS_CTA,
  AI_COMPETITORS_RESULTS_STEP_NUMBER,
  AI_COMPETITORS_RESULTS_SUMMARY,
  AI_COMPETITORS_RESULTS_TITLE,
} from "../../../i18n/keysTranslations";
import { AI_COMPETITORS_PRIMARY_BUTTON_SX } from "../aiCompetitorsConstants";
import { markLockedAds, pickBalancedPreviewAds } from "../aiCompetitorsAds";
import AiCompetitorsStepIntro from "./AiCompetitorsStepIntro";
import AiCompetitorsAdCard from "./AiCompetitorsAdCard";

const getScrollGateIndex = (ads, isMobile) => {
  const firstLockedIndex = ads.findIndex((ad) => ad.isLocked);
  if (firstLockedIndex < 0) return -1;
  const extraLocked = isMobile ? 2 : 3;
  return Math.min(firstLockedIndex + extraLocked, ads.length - 1);
};

const isGateInView = (node) => {
  if (!node) return false;
  const rect = node.getBoundingClientRect();
  const viewportHeight = window.innerHeight || 0;
  return rect.top < viewportHeight * 0.72;
};

const VIDEO_PLAYER_EVENT_SELECTOR = [
  "video",
  "audio",
  ".gallery-video-mute-toggle",
  ".competitor-ad-library-carousel-controls",
].join(", ");

const VIDEO_PLAYER_CLICK_GUARD_MS = 1200;

const isVideoPlayerEvent = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== "function") {
    return false;
  }
  return Boolean(target.closest(VIDEO_PLAYER_EVENT_SELECTOR));
};

const AiCompetitorsResultsStep = ({
  competitors = [],
  ads = [],
  adsSummary,
  onGoToAgenda,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gateRef = useRef(null);
  const hasOpenedAgendaRef = useRef(false);
  const isPromptingAgendaRef = useRef(false);
  const isScrollAgendaEnabledRef = useRef(true);
  const lastVideoPlayerEventAtRef = useRef(0);
  const [unmutedVideoKey, setUnmutedVideoKey] = useState(null);
  const adsCount = Math.max(Number(adsSummary?.adsCount || 0), ads.length);
  const competitorCount = adsSummary?.competitorCount ?? competitors.length;
  const previewAds = useMemo(
    () => pickBalancedPreviewAds(ads),
    [ads]
  );
  const markedAds = useMemo(() => markLockedAds(previewAds), [previewAds]);
  const lockedCount = markedAds.filter((ad) => ad.isLocked).length;
  const gateIndex = getScrollGateIndex(markedAds, isMobile);

  const requestAgenda = useCallback(
    async ({ fromScroll = false, fromAction = false } = {}) => {
      if (hasOpenedAgendaRef.current || isPromptingAgendaRef.current) {
        return;
      }
      if (fromScroll && !isScrollAgendaEnabledRef.current) {
        return;
      }
      if (
        !fromScroll &&
        !fromAction &&
        Date.now() - lastVideoPlayerEventAtRef.current <
          VIDEO_PLAYER_CLICK_GUARD_MS
      ) {
        return;
      }
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const restoreScroll = () => {
        window.scrollTo(scrollX, scrollY);
      };
      isPromptingAgendaRef.current = true;
      const result = await ConfirmAlert({
        title: t(AI_COMPETITORS_AGENDA_CONFIRM_TITLE),
        text: t(AI_COMPETITORS_AGENDA_CONFIRM_TEXT),
        confirmButtonText: t(AI_COMPETITORS_AGENDA_CONFIRM_CTA),
        showCancelButton: false,
        showCloseButton: true,
        icon: "info",
        heightAuto: false,
        returnFocus: false,
        scrollbarPadding: false,
        willClose: restoreScroll,
        didClose: restoreScroll,
      });
      isPromptingAgendaRef.current = false;
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      if (result?.isConfirmed) {
        hasOpenedAgendaRef.current = true;
        onGoToAgenda();
        return;
      }
      if (fromScroll) {
        isScrollAgendaEnabledRef.current = false;
      }
    },
    [onGoToAgenda, t]
  );

  const onRequestVideoUnmute = useCallback(({ videoMuteKey }) => {
    setUnmutedVideoKey(videoMuteKey);
  }, []);

  const onRequestVideoMute = useCallback(() => {
    setUnmutedVideoKey(null);
  }, []);

  const onResultsClickCapture = useCallback((event) => {
    if (!isVideoPlayerEvent(event)) {
      return;
    }
    lastVideoPlayerEventAtRef.current = Date.now();
  }, []);

  const onResultsClick = useCallback(
    (event) => {
      if (isVideoPlayerEvent(event)) {
        return;
      }
      requestAgenda();
    },
    [requestAgenda]
  );

  useEffect(() => {
    const sentinel = gateRef.current;
    if (!sentinel) return undefined;

    let userInteracted = false;

    const tryOpenFromScroll = () => {
      if (!userInteracted) return;
      if (isGateInView(sentinel)) {
        requestAgenda({ fromScroll: true });
      }
    };

    const markInteracted = () => {
      userInteracted = true;
      tryOpenFromScroll();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        tryOpenFromScroll();
      },
      { threshold: 0.01, rootMargin: "0px 0px -28% 0px" }
    );
    observer.observe(sentinel);

    window.addEventListener("wheel", markInteracted, { passive: true });
    window.addEventListener("touchmove", markInteracted, { passive: true });
    window.addEventListener("scroll", markInteracted, { passive: true });
    window.addEventListener("keydown", markInteracted);

    return () => {
      observer.disconnect();
      window.removeEventListener("wheel", markInteracted);
      window.removeEventListener("touchmove", markInteracted);
      window.removeEventListener("scroll", markInteracted);
      window.removeEventListener("keydown", markInteracted);
    };
  }, [gateIndex, markedAds.length, requestAgenda]);

  return (
    <Box
      onClick={onResultsClick}
      onClickCapture={onResultsClickCapture}
      sx={{ cursor: "pointer" }}
    >
      <AiCompetitorsStepIntro
        stepNumber={
          adsCount > 0 ? t(AI_COMPETITORS_RESULTS_STEP_NUMBER) : undefined
        }
        title={t(AI_COMPETITORS_RESULTS_TITLE)}
        mutedTitle={t(AI_COMPETITORS_RESULTS_MUTED)}
        description={
          adsCount > 0
            ? t(AI_COMPETITORS_RESULTS_SUMMARY, { adsCount, competitorCount })
            : undefined
        }
      />
      {adsCount === 0 ? (
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 2.5, sm: 4 },
            p: { xs: 2, sm: 3 },
            borderRadius: BORDER_RADIUS,
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            boxShadow: BOX_SHADOW_THEME,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography sx={{ fontSize: { xs: 16, sm: 20 }, fontWeight: 600 }}>
            {t(AI_COMPETITORS_RESULTS_NO_ADS_ALERT_TITLE)}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: 13, sm: 14 },
              lineHeight: 1.5,
              opacity: 0.8,
            }}
          >
            {t(AI_COMPETITORS_RESULTS_NO_ADS_ALERT_BODY, {
              productName: IS_ADLE_DOMAIN ? "Adle" : "Buzzly",
            })}
          </Typography>
          <Button
            variant="contained"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (hasOpenedAgendaRef.current || isPromptingAgendaRef.current) {
                return;
              }
              hasOpenedAgendaRef.current = true;
              onGoToAgenda();
            }}
            sx={{
              ...AI_COMPETITORS_PRIMARY_BUTTON_SX,
              mt: 2.5,
              bgcolor: "primary.contrastText",
              color: "primary.main",
              "&:hover": {
                bgcolor: "primary.contrastText",
                opacity: 0.9,
                boxShadow: "none",
              },
            }}
          >
            {t(AI_COMPETITORS_RESULTS_NO_ADS_CTA)}
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            mt: { xs: 2.5, sm: 4 },
            display: "grid",
            gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
          }}
        >
          {markedAds.map((ad, index) => (
            <Box key={ad.id || `${ad.competitor || "ad"}-${index}`} sx={{ position: "relative" }}>
              {index === gateIndex ? (
                <Box
                  ref={gateRef}
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 1,
                    height: 1,
                    pointerEvents: "none",
                  }}
                />
              ) : null}
              {ad.competitor ? (
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: { xs: 13, sm: 14 },
                    fontWeight: 600,
                    color: ad.isLocked ? "text.disabled" : "text.primary",
                  }}
                >
                  {ad.competitor}
                </Typography>
              ) : null}
              <AiCompetitorsAdCard
                item={ad}
                isLocked={ad.isLocked}
                lockedCount={lockedCount}
                videoMuteKey={`${ad.id || `${ad.competitor || "ad"}-${index}`}::${ad?.url || ""}`}
                unmutedVideoKey={unmutedVideoKey}
                onRequestVideoUnmute={onRequestVideoUnmute}
                onRequestVideoMute={onRequestVideoMute}
                onGoToAgenda={requestAgenda}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AiCompetitorsResultsStep;
